/**
 * AQ-Cloud2 sidebar / header nav item.
 *
 * Renders a Link-wrapped entry that:
 *   • Respects required permissions (returns null if denied).
 *   • Computes active state from the current route.
 *   • Applies the brand active-state style (3px brand-accent left bar +
 *     muted background + bold brand-colored label).
 *   • Collapses to icon-only with a hover tooltip on the rail variant.
 *
 * The optional `group` field lets feature modules self-declare which
 * sidebar group they belong to (Workspace / Insights / Admin). When omitted
 * AppLayout treats it as 'workspace'.
 */
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { usePermissions } from '@/hooks/usePermissions'
import { isItemVisible } from '@/components/layout/navGroups'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export type NavGroupId = 'workspace' | 'insights' | 'admin'

export interface NavItemConfig {
  path: string
  label: string
  icon?: LucideIcon
  /** Optional sidebar group. Defaults to 'workspace'. */
  group?: NavGroupId
  /** Required permissions (supports wildcards: 'admin:*', '*:*') */
  requiredPermissions?: string[]
  /** @deprecated Use requiredPermissions instead */
  requiredScopes?: string[]
}

interface NavItemProps {
  item: NavItemConfig
  /** Render as an icon-only rail entry. */
  collapsed?: boolean
  /** Render with mobile-drawer touch sizes (44px target). */
  mobile?: boolean
}

export const NavItem = ({ item, collapsed = false, mobile = false }: NavItemProps): JSX.Element | null => {
  const location = useLocation()
  const { permissions } = usePermissions()

  if (!isItemVisible(item, permissions)) {
    return null
  }

  const isActive =
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  const Icon = item.icon

  if (collapsed) {
    return (
      <Link
        to={item.path}
        title={item.label}
        aria-label={item.label}
        // The collapsed rail conveys active state by colour alone; without this a
        // screen-reader user cannot tell which nav item is current.
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'mx-auto inline-flex items-center justify-center w-10 h-10 rounded transition-colors duration-fast',
          isActive
            ? 'bg-muted text-brand'
            : 'text-foreground/80 hover:bg-muted hover:text-foreground',
        )}
      >
        {Icon && <Icon className="h-[20px] w-[20px]" aria-hidden="true" />}
      </Link>
    )
  }

  return (
    <Link
      to={item.path}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'relative flex items-center gap-3 rounded transition-colors duration-fast',
        mobile ? 'h-11 px-3 text-sm' : 'h-9 px-3 text-[13px]',
        isActive
          ? 'bg-muted text-brand font-semibold'
          : 'text-foreground/85 hover:bg-muted hover:text-foreground',
      )}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute -left-3 top-1.5 bottom-1.5 w-[3px] rounded-sm bg-brand-accent"
        />
      )}
      {Icon && (
        <Icon
          className={cn(mobile ? 'h-5 w-5 shrink-0' : 'h-[18px] w-[18px] shrink-0')}
          aria-hidden="true"
        />
      )}
      <span className="truncate">{item.label}</span>
    </Link>
  )
}
